import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { jwtDecode } from 'jwt-decode';
import CollegeRecipientSelector from './components/CollegeRecipientSelector';
import theme from './theme';

const Container = styled.div`
  background: ${theme.colors.card};
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid ${theme.colors.border};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: 1.8rem;
  margin: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid ${theme.colors.inputBorder};
  background: ${theme.colors.inputBackground};
  color: ${theme.colors.text.primary};
  font-size: 1rem;

  option {
    background: ${theme.colors.backgroundSolid};
    color: ${theme.colors.text.primary};
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid ${theme.colors.inputBorder};
  background: ${theme.colors.inputBackground};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
  width: 300px;

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const StudentGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const StudentCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const StudentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const StudentInfo = styled.div`
  flex: 1;
`;

const StudentName = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: 1.3rem;
  margin: 0 0 0.5rem 0;
`;

const StudentEmail = styled.p`
  color: ${theme.colors.text.secondary};
  margin: 0 0 0.5rem 0;
`;

const StudentDetails = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const StatusBadge = styled.span`
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.status) {
      case 'verified':
        return 'background: #DCFCE7; color: #166534;';
      case 'rejected':
        return 'background: #FEE2E2; color: #991B1B;';
      case 'pending':
        return 'background: #FEF3C7; color: #92400E;';
      case 'pending_additional':
        return 'background: #DBEAFE; color: #1E40AF;';
      case 'unverified':
        return 'background: #F3F4F6; color: #374151;';
      default:
        return 'background: #F3F4F6; color: #374151;';
    }
  }}
`;

const WhatsAppBadge = styled.span`
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;

  ${props => {
    if (props.enabled === true) {
      return 'background: #DCFCE7; color: #166534;';
    } else {
      return 'background: #FEE2E2; color: #991B1B;';
    }
  }}
`;

const IdCardContainer = styled.div`
  margin: 1rem 0;
`;

const IdCardImage = styled.img`
  max-width: 300px;
  max-height: 200px;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !['variant'].includes(prop)
})`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  ${props => {
    if (props.variant === 'approve') {
      return 'background: #27ae60; color: white; &:hover { background: #229954; }';
    } else if (props.variant === 'reject') {
      return 'background: #e74c3c; color: white; &:hover { background: #c0392b; }';
    } else {
      return 'background: rgba(255, 255, 255, 0.2); color: white; &:hover { background: rgba(255, 255, 255, 0.3); }';
    }
  }}
`;

const RejectionModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ModalTitle = styled.h3`
  color: white;
  margin: 0 0 1rem 0;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const AdditionalInfoModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const BulkEmailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const BulkEmailContent = styled.div`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const BulkSMSModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const BulkSMSContent = styled.div`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const SMSModeSection = styled.div`
  margin-bottom: 2rem;
`;

const SMSMessageTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  margin-bottom: 0.5rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const SMSCharacterCount = styled.div`
  text-align: right;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1.5rem;
`;

const SMSSegmentInfo = styled.div`
  background: rgba(102, 126, 234, 0.2);
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #667eea;
  color: white;
  font-size: 0.9rem;
`;

const SendSMSButton = styled.button`
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(245, 87, 108, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmailModeSection = styled.div`
  margin-bottom: 2rem;
`;

const Label = styled.label`
  display: block;
  color: white;
  font-weight: 500;
  margin-bottom: 0.75rem;
  font-size: 1rem;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  cursor: pointer;
  font-size: 1rem;
`;

const RadioInput = styled.input`
  accent-color: #667eea;
`;

const StudentSelector = styled.div`
  margin-bottom: 1.5rem;
`;

const StudentSearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  margin-bottom: 1rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const StudentList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StudentCheckbox = styled.input`
  accent-color: #667eea;
`;

const EmailStudentInfo = styled.div`
  flex: 1;
`;

const EmailStudentName = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const EmailStudentEmail = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;

const SubjectInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  margin-bottom: 0.5rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1.5rem;
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  min-height: 200px;
  resize: vertical;
  margin-bottom: 1.5rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const SignaturePreview = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid #667eea;
`;

const SignatureTitle = styled.div`
  font-weight: 600;
  color: #667eea;
  margin-bottom: 0.5rem;
`;

const SignatureContent = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const InfoText = styled.div`
  background: rgba(52, 152, 219, 0.2);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #3498db;
  color: white;
  font-size: 0.95rem;
`;

const SendEmailButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: white;
  font-size: 1.2rem;
`;

const InfoDisplay = styled.div`
  background: rgba(52, 152, 219, 0.2);
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  border-left: 4px solid #3498db;
`;

const InfoTitle = styled.strong`
  color: #3498db;
  display: block;
  margin-bottom: 0.5rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const PaginationInfo = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const PageInput = styled.input`
  width: 60px;
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  text-align: center;
  font-size: 0.9rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const PageSizeSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.9rem;
  cursor: pointer;

  option {
    background: #2a5298;
  }
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function StudentVerification({ showMessage, onStudentUpdate }) {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [whatsappFilter, setWhatsappFilter] = useState('all');
  const [collegeFilter, setCollegeFilter] = useState('');
  const [colleges, setColleges] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectModal, setRejectModal] = useState({ open: false, studentId: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [additionalInfoModal, setAdditionalInfoModal] = useState({ open: false, studentId: null });
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [editingStudent, setEditingStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteModal, setDeleteModal] = useState({ open: false, studentId: null, studentName: '' });
  const [bulkEmailModal, setBulkEmailModal] = useState({ open: false });
  const [emailRecipientType, setEmailRecipientType] = useState('ALL'); // NEW: ALL, SELECTED_COLLEGES, SINGLE_COLLEGE, INDIVIDUAL_STUDENTS
  const [emailSelectedColleges, setEmailSelectedColleges] = useState([]); // NEW: selected college IDs
  const [emailCollegeError, setEmailCollegeError] = useState(''); // NEW: validation error for colleges
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [bulkSMSModal, setBulkSMSModal] = useState({ open: false });
  const [smsMode, setSMSMode] = useState('all');
  const [smsMessage, setSMSMessage] = useState('');
  const [selectedStudentsForSMS, setSelectedStudentsForSMS] = useState([]);
  const [studentSearchTermForSMS, setStudentSearchTermForSMS] = useState('');
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
    hasNextPage: false,
    hasPreviousPage: false
  });

  const fetchStudents = useCallback(async (page = 1, limit = 20, verificationStatus = null, collegeId = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      let url = `${API_BASE_URL}/api/v1/admin/students?page=${page}&limit=${limit}`;
      if (verificationStatus && verificationStatus !== 'all') {
        url += `&verification_status=${verificationStatus}`;
      }
      if (collegeId) {
        url += `&college_id=${collegeId}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStudents(data.data || []);
        setPagination(data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: 20,
          hasNextPage: false,
          hasPreviousPage: false
        });
      } else {
        const error = await response.json();
        const errorMessage = typeof error.error === 'string' ? error.error : error.error?.message || 'Failed to fetch students';
        showMessage(errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = error.message || 'Network error';
      showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const filterStudents = useCallback(() => {
    let filtered = students;

    if (searchTerm) {
      filtered = filtered.filter(student =>
        student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.mobile?.includes(searchTerm) ||
        student.course_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.roll_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (whatsappFilter !== 'all') {
      filtered = filtered.filter(student => {
        const whatsappEnabled = student.whatsapp_opt_in === true || student.whatsapp_opt_in === 'true';
        if (whatsappFilter === 'enabled') return whatsappEnabled;
        if (whatsappFilter === 'disabled') return !whatsappEnabled;
        return true;
      });
    }

    setFilteredStudents(filtered);
  }, [students, searchTerm, whatsappFilter]);

  useEffect(() => {
    fetchStudents(pagination.currentPage, pagination.limit, statusFilter, collegeFilter);
  }, [fetchStudents, pagination.currentPage, pagination.limit, statusFilter, collegeFilter]);

  useEffect(() => {
    filterStudents();
  }, [filterStudents]);

  const fetchColleges = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/colleges`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setColleges(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch colleges:', error);
    }
  }, []);

  useEffect(() => {
    fetchColleges();
  }, [fetchColleges]);

  const handleVerify = async (studentId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/students/${studentId}/verify`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        showMessage('Student verified successfully', 'success');
        fetchStudents(pagination.currentPage, pagination.limit, statusFilter);
        if (onStudentUpdate) onStudentUpdate(); // Refresh unverified count
      } else {
        showMessage('Failed to verify student', 'error');
      }
    } catch (error) {
      showMessage('Error verifying student', 'error');
    }
  };

  const handleReject = async (studentId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/students/${studentId}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rejection_reason: rejectionReason })
      });

      if (response.ok) {
        showMessage('Student rejected successfully', 'success');
        setRejectModal({ open: false, studentId: null });
        setRejectionReason('');
        fetchStudents(pagination.currentPage, pagination.limit, statusFilter);
        if (onStudentUpdate) onStudentUpdate(); // Refresh unverified count
      } else {
        showMessage('Failed to reject student', 'error');
      }
    } catch (error) {
      showMessage('Error rejecting student', 'error');
    }
  };

  const openRejectModal = (studentId) => {
    setRejectModal({ open: true, studentId });
  };

  const handleRequestAdditionalInfo = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/students/${additionalInfoModal.studentId}/request-additional-info`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ additional_info: additionalInfo })
      });

      if (response.ok) {
        showMessage('Additional information requested successfully', 'success');
        setAdditionalInfoModal({ open: false, studentId: null });
        setAdditionalInfo('');
        fetchStudents(pagination.currentPage, pagination.limit, statusFilter);
      } else {
        showMessage('Failed to request additional information', 'error');
      }
    } catch (error) {
      showMessage('Error requesting additional information', 'error');
    }
  };

  const openAdditionalInfoModal = (studentId) => {
    setAdditionalInfoModal({ open: true, studentId });
  };

  const closeAdditionalInfoModal = () => {
    setAdditionalInfoModal({ open: false, studentId: null });
    setAdditionalInfo('');
  };

  const closeRejectModal = () => {
    setRejectModal({ open: false, studentId: null });
    setRejectionReason('');
  };

  const handleDelete = (studentId, studentName) => {
    setDeleteModal({ open: true, studentId, studentName });
  };

  const confirmDelete = async () => {
    if (!deleteModal.studentId) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/students/${deleteModal.studentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        showMessage('Student deleted successfully', 'success');
        fetchStudents(pagination.currentPage, pagination.limit, statusFilter);
        if (onStudentUpdate) onStudentUpdate(); // Refresh unverified count
      } else {
        showMessage('Failed to delete student', 'error');
      }
    } catch (error) {
      showMessage('Error deleting student', 'error');
    } finally {
      setDeleteModal({ open: false, studentId: null, studentName: '' });
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, studentId: null, studentName: '' });
  };

  const openBulkEmailModal = () => {
    setBulkEmailModal({ open: true });
    setEmailRecipientType('ALL'); // NEW
    setEmailSelectedColleges([]); // NEW
    setEmailCollegeError(''); // NEW
    setEmailSubject('');
    setEmailMessage('');
    setSelectedStudents([]);
    setStudentSearchTerm('');
  };

  const closeBulkEmailModal = () => {
    setBulkEmailModal({ open: false });
    setEmailRecipientType('ALL'); // NEW
    setEmailSelectedColleges([]); // NEW
    setEmailCollegeError(''); // NEW
    setEmailSubject('');
    setEmailMessage('');
    setSelectedStudents([]);
    setStudentSearchTerm('');
  };

  const openBulkSMSModal = () => {
    setBulkSMSModal({ open: true });
    setSMSMode('all');
    setSMSMessage('');
    setSelectedStudentsForSMS([]);
    setStudentSearchTermForSMS('');
  };

  const closeBulkSMSModal = () => {
    setBulkSMSModal({ open: false });
  };

  const handleStudentSelectionForSMS = (studentId) => {
    setSelectedStudentsForSMS(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const filteredStudentsForSMS = students.filter(student =>
    student.full_name.toLowerCase().includes(studentSearchTermForSMS.toLowerCase()) ||
    student.email.toLowerCase().includes(studentSearchTermForSMS.toLowerCase())
  );

  const validateSMSForm = () => {
    if (!smsMessage.trim() || smsMessage.length < 1) {
      showMessage('Message is required', 'error');
      return false;
    }
    if (smsMessage.length > 1600) {
      showMessage('Message must be 1600 characters or less', 'error');
      return false;
    }
    if (smsMode === 'selected' && selectedStudentsForSMS.length === 0) {
      showMessage('Please select at least one student', 'error');
      return false;
    }
    return true;
  };

  const handleSendBulkSMS = async () => {
    if (!validateSMSForm()) return;

    setIsSendingSMS(true);
    
    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      if (!token) {
        showMessage('Authentication token not found. Please log in again.', 'error');
        return;
      }

      // Check if token is expired
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp <= currentTime) {
          showMessage('Your session has expired. Please log in again.', 'error');
          localStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminToken');
          return;
        }
      } catch (decodeError) {
        showMessage('Invalid authentication token. Please log in again.', 'error');
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        return;
      }
      
      const payload = {
        mode: smsMode,
        message: smsMessage.trim()
      };

      if (smsMode === 'selected') {
        payload.student_ids = selectedStudentsForSMS;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/admin/bulk-sms/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        const messagesSent = data.data?.messages_sent || 0;
        const messagesFailed = data.data?.messages_failed || 0;
        const excludedCount = data.data?.excluded_count || 0;
        const excludedReason = data.data?.excluded_reason || '';
        
        let message = `SMS campaign completed. Sent: ${messagesSent}, Failed: ${messagesFailed}`;
        if (excludedCount > 0) {
          message += `. Excluded: ${excludedCount} (${excludedReason})`;
        }
        
        showMessage(message, messagesFailed > 0 ? 'warning' : 'success');
        closeBulkSMSModal();
      } else {
        const errorMessage = data.message || data.error || 'Failed to send SMS';
        showMessage(errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = error.message || 'Network error. Please try again.';
      showMessage(errorMessage, 'error');
    } finally {
      setIsSendingSMS(false);
    }
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const filteredStudentsForEmail = students.filter(student =>
    student.full_name.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(studentSearchTerm.toLowerCase())
  );

  const validateEmailForm = () => {
    // Validate subject
    if (!emailSubject.trim() || emailSubject.length < 3) {
      showMessage('Subject must be at least 3 characters long', 'error');
      return false;
    }
    if (emailSubject.length > 200) {
      showMessage('Subject must be less than 200 characters', 'error');
      return false;
    }

    // Validate message
    if (!emailMessage.trim() || emailMessage.length < 10) {
      showMessage('Message must be at least 10 characters long', 'error');
      return false;
    }

    // Validate recipient type specific requirements
    if (emailRecipientType === 'SELECTED_COLLEGES' && emailSelectedColleges.length === 0) {
      setEmailCollegeError('Please select at least one college');
      showMessage('Please select at least one college', 'error');
      return false;
    }

    if (emailRecipientType === 'SINGLE_COLLEGE' && emailSelectedColleges.length === 0) {
      setEmailCollegeError('Please select a college');
      showMessage('Please select a college', 'error');
      return false;
    }

    if (emailRecipientType === 'INDIVIDUAL_STUDENTS' && selectedStudents.length === 0) {
      showMessage('Please select at least one student', 'error');
      return false;
    }

    setEmailCollegeError('');
    return true;
  };

  const handleSendBulkEmail = async () => {
    if (!validateEmailForm()) return;

    setIsSendingEmail(true);
    
    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      if (!token) {
        showMessage('Authentication token not found. Please log in again.', 'error');
        return;
      }

      // Check if token is expired
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp <= currentTime) {
          showMessage('Your session has expired. Please log in again.', 'error');
          localStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminToken');
          return;
        }
      } catch (decodeError) {
        showMessage('Invalid authentication token. Please log in again.', 'error');
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        return;
      }
      
      const payload = {
        recipientType: emailRecipientType,
        subject: emailSubject.trim(),
        message: emailMessage.trim()
      };

      // Add recipient-specific data
      if (emailRecipientType === 'SELECTED_COLLEGES' || emailRecipientType === 'SINGLE_COLLEGE') {
        payload.college_ids = emailSelectedColleges;
      } else if (emailRecipientType === 'INDIVIDUAL_STUDENTS') {
        payload.student_ids = selectedStudents;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/admin/bulk-email/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        const emailsSent = data.data?.emails_sent || 0;
        const emailsFailed = data.data?.emails_failed || 0;
        showMessage(
          `Emails processed successfully. Sent: ${emailsSent}, Failed: ${emailsFailed}`,
          emailsFailed > 0 ? 'warning' : 'success'
        );
        closeBulkEmailModal();
      } else {
        const errorMessage = data.message || data.error || 'Failed to send emails';
        showMessage(errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = error.message || 'Network error. Please try again.';
      showMessage(errorMessage, 'error');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setEditForm({
      full_name: student.full_name,
      email: student.email,
      mobile: student.mobile,
      college_name: student.college_name,
      university_name: student.university_name,
      course_name: student.course_name,
      start_year: student.start_year,
      end_year: student.end_year,
      roll_number: student.roll_number,
      student_id_url: student.student_id_url
    });
  };

  const handleSaveEdit = async () => {
    if (!editingStudent) return;

    // Ensure all values are strings and not empty
    const formData = {
      full_name: String(editForm.full_name || '').trim(),
      email: String(editForm.email || '').trim(),
      mobile: String(editForm.mobile || '').trim(),
      college_name: String(editForm.college_name || '').trim(),
      university_name: String(editForm.university_name || '').trim(),
      course_name: String(editForm.course_name || '').trim(),
      start_year: String(editForm.start_year || '').trim(),
      end_year: String(editForm.end_year || '').trim(),
      roll_number: String(editForm.roll_number || '').trim(),
      student_id_url: String(editForm.student_id_url || '').trim()
    };

    // Only include non-empty fields
    const cleanFormData = Object.fromEntries(
      Object.entries(formData).filter(([key, value]) => value !== '')
    );

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/students/${editingStudent.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanFormData)
      });

      const data = await response.json();

      if (data.success) {
        showMessage('Student updated successfully', 'success');
        setEditingStudent(null);
        setEditForm({});
        fetchStudents(pagination.currentPage, pagination.limit, statusFilter);
        if (onStudentUpdate) onStudentUpdate(); // Refresh unverified count
      } else {
        showMessage('Failed to update student', 'error');
      }
    } catch (error) {
      showMessage('Error updating student', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingStudent(null);
    setEditForm({});
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handlePageSizeChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: parseInt(newLimit), currentPage: 1 }));
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
          Loading students...
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <>
      <Container>
      <Header>
        <Title>Student Verification</Title>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <SendSMSButton onClick={openBulkSMSModal}>
            📱 Send SMS
          </SendSMSButton>
          <SendEmailButton onClick={openBulkEmailModal}>
            📧 Send Email
          </SendEmailButton>
        </div>
      </Header>

      <FilterContainer>
        <FilterSelect
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="unverified">Unverified</option>
          <option value="pending">Pending</option>
          <option value="pending_additional">Pending Additional</option>
          <option value="verified">Verified</option>
          <option value="rejected">Rejected</option>
        </FilterSelect>

        <FilterSelect
          value={whatsappFilter}
          onChange={(e) => setWhatsappFilter(e.target.value)}
        >
          <option value="all">All WhatsApp</option>
          <option value="enabled">🟢 Enabled</option>
          <option value="disabled">🔴 Disabled</option>
        </FilterSelect>

        <FilterSelect
          value={collegeFilter}
          onChange={(e) => setCollegeFilter(e.target.value)}
        >
          <option value="">All Colleges</option>
          {colleges.map(college => (
            <option key={college.id} value={college.id}>
              {college.name} {college.city && `(${college.city})`}
            </option>
          ))}
        </FilterSelect>

          <PageSizeSelect
          value={pagination.limit}
          onChange={(e) => handlePageSizeChange(e.target.value)}
        >
          <option value="10">10 per page</option>
          <option value="20">20 per page</option>
          <option value="50">50 per page</option>
          <option value="100">100 per page</option>
        </PageSizeSelect>
        
        <SearchInput
          type="text"
          placeholder="Search by name, email, college, or university..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        
      </FilterContainer>

      <StudentGrid>
        {filteredStudents.map(student => (
          <StudentCard key={student.id}>
            <StudentHeader>
              <StudentInfo>
                <StudentName>{student.full_name}</StudentName>
                <StudentEmail>{student.email}</StudentEmail>
                <StudentDetails>
                  <div>📱 {student.mobile || 'Not provided'}</div>
                  <div>🎓 {student.college_name || 'Not provided'}</div>
                  <div>🏛️ {student.university_name || 'Not provided'}</div>
                  <div>📚 {student.course_name || 'Not provided'}</div>
                  <div>📅 {student.start_year && student.end_year ? `${student.start_year} - ${student.end_year}` : 'Not provided'}</div>
                  <div>🆔 {student.roll_number || 'Not provided'}</div>
                  <div>📅 Joined: {new Date(student.created_at).toLocaleDateString()}</div>
                  <div>
                    <WhatsAppBadge enabled={student.whatsapp_opt_in === true || student.whatsapp_opt_in === 'true'}>
                      {(student.whatsapp_opt_in === true || student.whatsapp_opt_in === 'true') ? '🟢 WhatsApp Enabled' : '🔴 WhatsApp Disabled'}
                    </WhatsAppBadge>
                  </div>
                </StudentDetails>
              </StudentInfo>
              <StatusBadge status={student.verification_status}>
                {student.verification_status}
              </StatusBadge>
            </StudentHeader>

            {student.student_id_url && (
              <IdCardContainer>
                <h4 style={{ color: 'white', margin: '0 0 0.5rem 0' }}>Student ID Card:</h4>
                <IdCardImage 
                  src={student.student_id_url} 
                  alt="Student ID Card"
                  onClick={() => window.open(student.student_id_url, '_blank')}
                />
              </IdCardContainer>
            )}

            <div style={{ 
              padding: '1rem', 
              background: 'rgba(102, 126, 234, 0.1)', 
              borderRadius: '8px', 
              marginTop: '1rem',
              border: '1px solid rgba(102, 126, 234, 0.3)'
            }}>
              <h4 style={{ color: 'white', margin: '0 0 0.75rem 0', fontSize: '0.95rem' }}>Communication Preferences</h4>
              <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', lineHeight: '1.8' }}>
                <div>
                  <strong>Terms Accepted:</strong> {(student.terms_accepted === true || student.terms_accepted === 'true') ? '✔ Yes' : '✖ No'}
                </div>
                {student.terms_accepted_at && (
                  <div style={{ marginLeft: '1rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Accepted On: {new Date(student.terms_accepted_at).toLocaleDateString()}
                  </div>
                )}
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>WhatsApp Updates:</strong> {(student.whatsapp_opt_in === true || student.whatsapp_opt_in === 'true') ? '✔ Enabled' : '✖ Disabled'}
                </div>
                {student.whatsapp_opt_in_at && (
                  <div style={{ marginLeft: '1rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Opt-in Date: {new Date(student.whatsapp_opt_in_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {student.verification_status === 'unverified' || student.verification_status === 'pending' ? (
              <ActionButtons>
                <Button 
                  variant="approve" 
                  onClick={() => handleVerify(student.id)}
                >
                  ✅ Verify
                </Button>
                <Button 
                  variant="reject" 
                  onClick={() => openRejectModal(student.id)}
                >
                  ❌ Reject
                </Button>
                <Button 
                  onClick={() => openAdditionalInfoModal(student.id)}
                >
                  📋 Request Info
                </Button>
                <Button 
                  onClick={() => handleEdit(student)}
                >
                  ✏️ Edit
                </Button>
                <Button 
                  variant="reject" 
                  onClick={() => handleDelete(student.id, student.full_name)}
                >
                  🗑️ Delete
                </Button>
              </ActionButtons>
            ) : student.verification_status === 'verified' ? (
              <ActionButtons>
                <Button 
                  onClick={() => handleEdit(student)}
                >
                  ✏️ Edit
                </Button>
                <Button 
                  variant="reject" 
                  onClick={() => handleDelete(student.id, student.full_name)}
                >
                  🗑️ Delete
                </Button>
              </ActionButtons>
            ) : student.verification_status === 'rejected' && student.rejection_reason ? (
              <InfoDisplay>
                <InfoTitle>Rejection Reason:</InfoTitle>
                <InfoText>{student.rejection_reason}</InfoText>
              </InfoDisplay>
            ) : student.verification_status === 'pending_additional' && student.rejection_reason ? (
              <>
                <InfoDisplay>
                  <InfoTitle>Additional Information Requested:</InfoTitle>
                  <InfoText>{student.rejection_reason}</InfoText>
                </InfoDisplay>
                <ActionButtons>
                  <Button 
                    onClick={() => handleEdit(student)}
                  >
                    ✏️ Edit
                  </Button>
                  <Button 
                    variant="reject" 
                    onClick={() => handleDelete(student.id, student.full_name)}
                  >
                    🗑️ Delete
                  </Button>
                </ActionButtons>
              </>
            ) : null}
          
          </StudentCard>
        ))}
      </StudentGrid>

      <PaginationContainer>
        <PaginationInfo>
          Page {pagination.currentPage} of {pagination.totalPages} | Total: {pagination.totalCount} students
        </PaginationInfo>
        <PaginationControls>
          <PageButton
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPreviousPage}
          >
            Previous
          </PageButton>
          <PageInput
            type="number"
            min="1"
            max={pagination.totalPages}
            value={pagination.currentPage}
            onChange={(e) => {
              const page = parseInt(e.target.value);
              if (page >= 1 && page <= pagination.totalPages) {
                handlePageChange(page);
              }
            }}
          />
          <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>/ {pagination.totalPages}</span>
          <PageButton
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNextPage}
          >
            Next
          </PageButton>
        </PaginationControls>
      </PaginationContainer>

      {rejectModal.open && (
        <RejectionModal>
          <ModalContent>
            <ModalTitle>Reject Student Application</ModalTitle>
            <ModalTextarea
              placeholder="Please provide a reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <ModalButtons>
              <Button onClick={closeRejectModal}>Cancel</Button>
              <Button
                variant="reject"
                onClick={() => handleReject(rejectModal.studentId)}
                disabled={!rejectionReason.trim()}
                >
                Reject Application
              </Button>
            </ModalButtons>
          </ModalContent>
        </RejectionModal>
      )}

      {additionalInfoModal.open && (
        <AdditionalInfoModal>
          <ModalContent>
            <ModalTitle>Request Additional Information</ModalTitle>
            <ModalTextarea
              placeholder="Please specify what additional information or documents are required..."
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
            />
            <ModalButtons>
              <Button onClick={closeAdditionalInfoModal}>Cancel</Button>
              <Button 
                variant="approve" 
                onClick={handleRequestAdditionalInfo}
                disabled={!additionalInfo.trim()}
              >
                Send Request
              </Button>
            </ModalButtons>
          </ModalContent>
        </AdditionalInfoModal>
      )}
    </Container>

      {editingStudent && (
        <RejectionModal>
          <ModalContent>
            <ModalTitle>Edit Student Information</ModalTitle>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '1.5rem', 
              marginBottom: '1rem' 
            }}>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>Full Name:</label>
                <input
                  type="text"
                  value={editForm.full_name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>Email:</label>
                <input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>Mobile:</label>
                <input
                  type="tel"
                  value={editForm.mobile || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, mobile: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>College Name:</label>
                <input
                  type="text"
                  value={editForm.college_name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, college_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>University Name:</label>
                <input
                  type="text"
                  value={editForm.university_name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, university_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>Course Name:</label>
                <input
                  type="text"
                  value={editForm.course_name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, course_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>Start Year:</label>
                <input
                  type="text"
                  value={editForm.start_year || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, start_year: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>End Year:</label>
                <input
                  type="text"
                  value={editForm.end_year || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, end_year: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>Roll Number:</label>
                <input
                  type="text"
                  value={editForm.roll_number || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, roll_number: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>Student ID Card URL:</label>
                <input
                  type="text"
                  value={editForm.student_id_url || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, student_id_url: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }}
                />
              </div>
            </div>
            <ModalButtons>
              <Button onClick={handleCancelEdit}>Cancel</Button>
              <Button 
                variant="approve" 
                onClick={handleSaveEdit}
              >
                Save Changes
              </Button>
            </ModalButtons>
          </ModalContent>
        </RejectionModal>
      )}

      {deleteModal.open && (
        <RejectionModal>
          <ModalContent>
            <ModalTitle>Confirm Delete</ModalTitle>
            <div style={{ color: 'white', marginBottom: '1rem' }}>
              Are you sure you want to delete <strong>{deleteModal.studentName}</strong>? 
              This action cannot be undone.
            </div>
            <ModalButtons>
              <Button onClick={closeDeleteModal}>Cancel</Button>
              <Button 
                variant="reject" 
                onClick={confirmDelete}
              >
                Delete Student
              </Button>
            </ModalButtons>
          </ModalContent>
        </RejectionModal>
      )}

      {bulkEmailModal.open && (
        <BulkEmailModal>
          <BulkEmailContent>
            <ModalTitle>Send Bulk Email</ModalTitle>
            
            <EmailModeSection>
              <Label style={{ marginBottom: '1rem' }}>Recipient Type</Label>
              <RadioGroup>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="emailRecipientType"
                    value="ALL"
                    checked={emailRecipientType === 'ALL'}
                    onChange={(e) => {
                      setEmailRecipientType(e.target.value);
                      setEmailSelectedColleges([]);
                      setSelectedStudents([]);
                      setEmailCollegeError('');
                    }}
                  />
                  All Students
                </RadioLabel>
              </RadioGroup>
              
              <RadioGroup>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="emailRecipientType"
                    value="SELECTED_COLLEGES"
                    checked={emailRecipientType === 'SELECTED_COLLEGES'}
                    onChange={(e) => {
                      setEmailRecipientType(e.target.value);
                      setEmailSelectedColleges([]);
                      setSelectedStudents([]);
                      setEmailCollegeError('');
                    }}
                  />
                  Selected Colleges (Multi-select)
                </RadioLabel>
              </RadioGroup>

              <RadioGroup>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="emailRecipientType"
                    value="SINGLE_COLLEGE"
                    checked={emailRecipientType === 'SINGLE_COLLEGE'}
                    onChange={(e) => {
                      setEmailRecipientType(e.target.value);
                      setEmailSelectedColleges([]);
                      setSelectedStudents([]);
                      setEmailCollegeError('');
                    }}
                  />
                  Individual College
                </RadioLabel>
              </RadioGroup>

              <RadioGroup>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="emailRecipientType"
                    value="INDIVIDUAL_STUDENTS"
                    checked={emailRecipientType === 'INDIVIDUAL_STUDENTS'}
                    onChange={(e) => {
                      setEmailRecipientType(e.target.value);
                      setEmailSelectedColleges([]);
                      setSelectedStudents([]);
                      setEmailCollegeError('');
                    }}
                  />
                  Individual Students
                </RadioLabel>
              </RadioGroup>
              
              {emailRecipientType === 'ALL' && (
                <InfoText>
                  This email will be sent to all verified registered students.
                </InfoText>
              )}
            </EmailModeSection>

            {/* College Selector for SELECTED_COLLEGES and SINGLE_COLLEGE */}
            {(emailRecipientType === 'SELECTED_COLLEGES' || emailRecipientType === 'SINGLE_COLLEGE') && (
              <CollegeRecipientSelector
                mode={emailRecipientType === 'SINGLE_COLLEGE' ? 'single' : 'multi'}
                onSelection={setEmailSelectedColleges}
                selectedIds={emailSelectedColleges}
                error={emailCollegeError}
              />
            )}

            {/* Student Selector for INDIVIDUAL_STUDENTS */}
            {emailRecipientType === 'INDIVIDUAL_STUDENTS' && (
              <StudentSelector>
                <StudentSearchInput
                  type="text"
                  placeholder="Search students by name or email..."
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                />
                <StudentList>
                  {filteredStudentsForEmail.slice(0, 50).map(student => (
                    <StudentItem key={student.id} onClick={() => handleStudentSelection(student.id)}>
                      <StudentCheckbox
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={() => handleStudentSelection(student.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <EmailStudentInfo>
                        <EmailStudentName>{student.full_name}</EmailStudentName>
                        <EmailStudentEmail>{student.email}</EmailStudentEmail>
                      </EmailStudentInfo>
                    </StudentItem>
                  ))}
                  {filteredStudentsForEmail.length === 0 && (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                      No students found
                    </div>
                  )}
                </StudentList>
                {selectedStudents.length > 0 && (
                  <div style={{ marginTop: '0.5rem', color: '#667eea', fontSize: '0.9rem' }}>
                    {selectedStudents.length} student(s) selected
                  </div>
                )}
              </StudentSelector>
            )}

            <div>
              <SubjectInput
                type="text"
                placeholder="Email Subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                maxLength={200}
              />
              <CharacterCount>
                {emailSubject.length}/200 characters
              </CharacterCount>
            </div>

            <MessageTextarea
              placeholder="Write your message here..."
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
            />

            <SignaturePreview>
              <SignatureTitle>Signature Preview</SignatureTitle>
              <SignatureContent>
                Regards,<br/>
                Just Polymer Solutions<br/>
                Chem-PetChem Education Portal<br/>
                support@jpolymersolutions.com<br/>
                https://jpolymersolutions.com
              </SignatureContent>
            </SignaturePreview>

            <ModalButtons>
              <Button onClick={closeBulkEmailModal}>Cancel</Button>
              <SendEmailButton 
                onClick={handleSendBulkEmail}
                disabled={isSendingEmail}
              >
                {isSendingEmail ? (
                  <>
                    <LoadingSpinner />
                    Sending...
                  </>
                ) : (
                  'Send Email'
                )}
              </SendEmailButton>
            </ModalButtons>
          </BulkEmailContent>
        </BulkEmailModal>
      )}

      {bulkSMSModal.open && (
        <BulkSMSModal>
          <BulkSMSContent>
            <ModalTitle>Send Bulk SMS</ModalTitle>
            
            <SMSModeSection>
              <RadioGroup>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="smsMode"
                    value="all"
                    checked={smsMode === 'all'}
                    onChange={(e) => setSMSMode(e.target.value)}
                  />
                  All Students
                </RadioLabel>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="smsMode"
                    value="selected"
                    checked={smsMode === 'selected'}
                    onChange={(e) => setSMSMode(e.target.value)}
                  />
                  Selected Students
                </RadioLabel>
              </RadioGroup>
              
              {smsMode === 'all' && (
                <InfoText>
                  This SMS will be sent to all verified students with valid phone numbers.
                </InfoText>
              )}
            </SMSModeSection>

            {smsMode === 'selected' && (
              <StudentSelector>
                <StudentSearchInput
                  type="text"
                  placeholder="Search students by name or email..."
                  value={studentSearchTermForSMS}
                  onChange={(e) => setStudentSearchTermForSMS(e.target.value)}
                />
                <StudentList>
                  {filteredStudentsForSMS.slice(0, 50).map(student => (
                    <StudentItem key={student.id} onClick={() => handleStudentSelectionForSMS(student.id)}>
                      <StudentCheckbox
                        type="checkbox"
                        checked={selectedStudentsForSMS.includes(student.id)}
                        onChange={() => handleStudentSelectionForSMS(student.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <EmailStudentInfo>
                        <EmailStudentName>{student.full_name}</EmailStudentName>
                        <EmailStudentEmail>{student.email}</EmailStudentEmail>
                      </EmailStudentInfo>
                    </StudentItem>
                  ))}
                  {filteredStudentsForSMS.length === 0 && (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                      No students found
                    </div>
                  )}
                </StudentList>
                {selectedStudentsForSMS.length > 0 && (
                  <div style={{ marginTop: '0.5rem', color: '#667eea', fontSize: '0.9rem' }}>
                    {selectedStudentsForSMS.length} student(s) selected
                  </div>
                )}
              </StudentSelector>
            )}

            <SMSMessageTextarea
              placeholder="Write your SMS message here..."
              value={smsMessage}
              onChange={(e) => setSMSMessage(e.target.value)}
              maxLength={1600}
            />
            <SMSCharacterCount>
              {smsMessage.length}/1600 characters
            </SMSCharacterCount>

            <SMSSegmentInfo>
              <strong>ℹ️ SMS Segments:</strong> {Math.ceil(smsMessage.length / 160)} segment(s)
              <br />
              <small>Standard SMS is 160 characters. Longer messages are split into multiple segments.</small>
            </SMSSegmentInfo>

            <ModalButtons>
              <Button onClick={closeBulkSMSModal}>Cancel</Button>
              <SendSMSButton 
                onClick={handleSendBulkSMS}
                disabled={isSendingSMS}
              >
                {isSendingSMS ? (
                  <>
                    <LoadingSpinner />
                    Sending...
                  </>
                ) : (
                  'Send SMS'
                )}
              </SendSMSButton>
            </ModalButtons>
          </BulkSMSContent>
        </BulkSMSModal>
      )}
    </>
  );
}

export default StudentVerification;
